import React, { useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Chip,
    IconButton,
    Pagination,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { FeaturedProjectsTypesItem } from '../types/FeaturedProjectsTypes';

// ***** Adjust these values to control card and image sizes
const SIZING = {
    GRID_VIEW: {
        MAX_WIDTH: '100%',
        MIN_HEIGHT: '400px',     // Try 350px-450px
        IMAGE_HEIGHT: '300px',   // Try 250px-350px
    },
    LIST_VIEW: {
        MAX_WIDTH: '100%',
        MIN_HEIGHT: '250px',     // Try 200px-300px
        IMAGE_WIDTH: '300px',    // Try 250px-350px
    },
    ITEMS_PER_PAGE: 9,          // Adjust based on preference
    SPACING: {
        GRID: 4,                 // Material-UI spacing unit (32px)
        CONTENT: 2               // Material-UI spacing unit (16px)
    }
} as const;

// Base card styling
const BaseCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: theme.shadows[8],
    },
}));

// Grid view specific styling
const GridCard = styled(BaseCard)({
    display: 'flex',
    flexDirection: 'column',
});

const GridCardMedia = styled(CardMedia)({
    height: SIZING.GRID_VIEW.IMAGE_HEIGHT,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
});

// List view specific styling
const ListCard = styled(BaseCard)({
    display: 'flex',
    flexDirection: 'row',
    height: SIZING.LIST_VIEW.MIN_HEIGHT,
});

const ListCardMedia = styled(CardMedia)({
    width: SIZING.LIST_VIEW.IMAGE_WIDTH,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
});

interface ProjectCardProps {
    item: FeaturedProjectsTypesItem;
    viewType: 'grid' | 'list';
}

const ProjectCard: React.FC<ProjectCardProps> = ({ item, viewType }) => {
    const CardComponent = viewType === 'grid' ? GridCard : ListCard;
    const MediaComponent = viewType === 'grid' ? GridCardMedia : ListCardMedia;

    return (
        <CardComponent>
            <MediaComponent
                image={item.imagePath}
                title={item.title || 'Project Image'}
            />
            <CardContent sx={{ flexGrow: 1 }}>
                {item.title && (
                    <Typography gutterBottom variant="h6" component="h3">
                        {item.title}
                    </Typography>
                )}
                <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={viewType === 'list' ? {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    } : undefined}
                >
                    {item.description}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                    {item.tags && item.tags.map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                        />
                    ))}
                </Box>
            </CardContent>
        </CardComponent>
    );
};

// Loading skeleton components
const LoadingSkeleton: React.FC<{ viewType: 'grid' | 'list' }> = ({ viewType }) => {
    return viewType === 'grid' ? (
        <GridCard>
            <Skeleton variant="rectangular" height={SIZING.GRID_VIEW.IMAGE_HEIGHT} />
            <CardContent>
                <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
                {/* Replace the count prop with multiple Skeleton components */}
                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
            </CardContent>
        </GridCard>
    ) : (
        <ListCard>
            <Skeleton variant="rectangular" width={SIZING.LIST_VIEW.IMAGE_WIDTH} />
            <CardContent sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
                {/* Replace the count prop with multiple Skeleton components */}
                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
            </CardContent>
        </ListCard>
    );
};

const FeaturedProjectsPage: React.FC = () => {
    const [viewType, setViewType] = useState<'grid' | 'list'>('grid');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // Mock data - replace with actual data fetch
    const projects: FeaturedProjectsTypesItem[] = []; // Your data here
    const totalPages = Math.ceil(projects.length / SIZING.ITEMS_PER_PAGE);

    const handleViewChange = (
        event: React.MouseEvent<HTMLElement>,
        newView: 'grid' | 'list',
    ) => {
        if (newView !== null) {
            setViewType(newView);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        // Add your data fetching logic here
    };

    const currentProjects = projects.slice(
        (page - 1) * SIZING.ITEMS_PER_PAGE,
        page * SIZING.ITEMS_PER_PAGE
    );

    return (
        <Box sx={{ py: 6 }}>
            <Container maxWidth="xl">
                {/* Header with view toggle */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4
                }}>
                    <Typography variant="h4" component="h1">
                        Featured Projects
                    </Typography>
                    <ToggleButtonGroup
                        value={viewType}
                        exclusive
                        onChange={handleViewChange}
                        aria-label="view type"
                    >
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridViewIcon />
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="list view">
                            <ViewListIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Projects Grid/List */}
                <Grid
                    container
                    spacing={SIZING.SPACING.GRID}
                    sx={viewType === 'list' ? { flexDirection: 'column' } : undefined}
                >
                    {loading ? (
                        // Loading skeletons
                        Array.from(new Array(SIZING.ITEMS_PER_PAGE)).map((_, index) => (
                            <Grid
                                item
                                key={index}
                                xs={12}
                                sm={viewType === 'grid' ? 6 : 12}
                                md={viewType === 'grid' ? 4 : 12}
                            >
                                <LoadingSkeleton viewType={viewType} />
                            </Grid>
                        ))
                    ) : (
                        // Actual projects
                        currentProjects.map((item) => (
                            <Grid
                                item
                                key={item.id}
                                xs={12}
                                sm={viewType === 'grid' ? 6 : 12}
                                md={viewType === 'grid' ? 4 : 12}
                            >
                                <ProjectCard item={item} viewType={viewType} />
                            </Grid>
                        ))
                    )}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Stack alignItems="center" sx={{ mt: 4 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            size="large"
                        />
                    </Stack>
                )}
            </Container>
        </Box>
    );
};

export default FeaturedProjectsPage;